<template>
  <div>
    <div class="d-flex mt-8 mx-9" v-if="step === 3">
      <div>
        <h2 class="font-weight-medium" style="color: #0D0D0E">Influência</h2>
        <p class="mt-2">A influência ajusta o volume baseado no histórico para refletir seu planejamento. Você pode
          aplicar uma influência geral e, se necessário, ajustar itens específicos.</p>
      </div>
    </div>

    <div class="mx-6 d-flex" v-if="step === 3">
      <div class="mx-2 px-6 py-4 border-gray-400 bra-6 w-30-porcento">
        <h2 class="font-16 font-weight-medium" style="color: #0D0D0E">Influência geral</h2>
        <p>Aplica o mesmo ajuste para todos os itens do escopo.</p>

        <v-form v-model="form_valido" ref="form_ref">
          <div class="d-flex justify-center align-center w-70-porcento">
            <v-text-field
              class="mr-5 input-clean"
              v-model="form.influencia_geral"
              :rules="rules.rangeInfluencia"
              hide-details="auto"
              label="0"
              append-icon="$Percent"
              color="#39404B"
              outlined
              dense
              single-line
              maxlength="5"
              @keydown.enter.prevent
              @keydown="caracteresNaoPermitidos"
            />

            <v-btn
              class="vbtn-class bra-8 px-8 text-transform-none"
              color="#4075FF"
              height="50"
              @click="aplicarInfluenciaGeral"
            >
              <span style="color: #FFFFFF;">Aplicar</span>
            </v-btn>
          </div>
        </v-form>
      </div>

      <div class="mx-2 ml-5 px-6 py-4 border-gray-400 bra-6 w-70-porcento">
        <h2 class="font-16 font-weight-medium" style="color: #0D0D0E">Resumo dos ajustes</h2>

        <div class="d-flex mt-6 w-70-porcento">
          <div class="d-flex mr-7 flex-column">
            <SysTooltip2
              text="Quantidade de produtos do escopo que têm um ajuste próprio (diferente da influência geral)."
              top
            >
              <template #activator>
                <h3 class="font-14 font-weight-medium mb-1" style="color: #73787F">Itens ajustados</h3>
              </template>
            </SysTooltip2>
            <span class="font-18 font-weight-medium" style="color: #39404B">{{ resumo_ajustes.itens_ajustados }}</span>
          </div>

          <div class="d-flex mr-7 flex-column">
            <SysTooltip2
              text="Mostra o peso (valor) dos itens ajustados dentro do escopo. Itens com maior peso tendem a impactar mais o total. A porcentagem refere-se ao faturamento médio mensal dos produtos ajustados nos últimos 12 meses."
              top
            >
              <template #activator>
                <h3 class="font-14 font-weight-medium mb-1" style="color: #73787F">
                  Representatividade no escopo</h3>
              </template>
            </SysTooltip2>
            <span class="font-18 font-weight-medium" style="color: #39404B">{{
                resumo_ajustes.representatividade_escopo
              }}%</span>
          </div>

          <div class="d-flex mr-7 flex-column">
            <SysTooltip2
              text="O sistema compara dois cenários: 1. Todo mundo com a influência geral, 2. Influência geral + itens ajustados com seus percentuais próprios. A diferença entre eles é o “impacto adicional”. Ex.: influência geral +20%, mas você ajustou itens importantes para +50%. O pacote pode sair de “+20%” para “+24%”. Esse +4% é o impacto adicional."
              top
            >
              <template #activator>
                <h3 class="font-14 font-weight-medium mb-1" style="color: #73787F">
                  Impacto adicional dos ajustes</h3>
              </template>
            </SysTooltip2>
            <span class="font-18 font-weight-medium" style="color: #39404B">{{
                resumo_ajustes.impacto_adicional_ajuste
              }}%</span>
          </div>

          <div class="d-flex mr-7 flex-column">
            <SysTooltip2
              text="Soma do ajuste geral com o efeito dos ajustes por item no total do escopo."
              right
            >
              <template #activator>
                <h3 class="font-14 font-weight-medium mb-1" style="color: #73787F">
                  Influência total estimada</h3>
              </template>
            </SysTooltip2>
            <span class="font-18 font-weight-medium" style="color: #39404B">
              {{ resumo_ajustes.influencia_total_estimada }}%</span>
          </div>
        </div>
      </div>
    </div>

    <div class="mx-8 px-6 py-4 mt-6 border-gray-400 bra-6" v-if="step === 3">
      <SysTooltip2
        text="Substitui o ajuste geral apenas para estes item."
        right
      >
        <template #activator>
          <h2 class="font-16 font-weight-medium" style="color: #0D0D0E">Influência por item</h2>
        </template>
      </SysTooltip2>
      <p>Use somente quando um item precisa de um ajuste diferente do geral (ex.: item novo, promoção, ruptura
        esperada).</p>

      <div>
        <div class="w-20-porcento">
          <v-text-field
            v-model="pesquisa_termo"
            class="input-pesquisa"
            hide-details
            outlined
            dense
            clearable
            label="Buscar no escopo"
            prepend-inner-icon="$MagnifyingGlass"
          />
        </div>

        <div class="mt-5">
          <SysDataTable
            :search="pesquisa_termo"
            :headers="headers"
            :items="produtos_influencia"
            item-key="index"
            :height="130"
            class="pl-2"
            :item-height="55"
            :item-class="corLinha"
            hide-default-footer
          >
            <template #item.descricao_produto="{item}">
              <div class="d-flex justify-space-between align-center">
                {{ item.descricao_produto }}

                <span
                  class="font-12 bra-7"
                  :style="badgeStyle(item.influencia_badge)"
                >
                  {{ item.influencia_badge }}
                </span>
              </div>
            </template>

            <template #item.percentual_influencia="{ item }">
              <span>
                {{ item.percentual_influencia }} %
              </span>
            </template>

            <template #item.acoes="{item}">
              <v-icon
                color="#39404B"
                class="mr-2"
                size="28"
                alt="minus-circle.btn"
                @click="removerItemInfluencia(item)"
              >
                $MinusCircle
              </v-icon>

              <v-icon
                color="#39404B"
                class="ml-2"
                size="28"
                alt="pencil-simple.btn"
                @click="editarItemInfluencia(item)"
              >
                $PencilSimple
              </v-icon>
            </template>
          </SysDataTable>
        </div>
      </div>
    </div>

    <DialogEditarInfluenciaProduto
      v-model="dialog_editar_influencia"
      :produto="form.produto"
      @fechar="dialog_editar_influencia = false;"
      @confirmar="(valor) => aplicarInfluenciaEspecifica(valor)"
    />
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { toast2 } from "@/components/utils/ui_v4/alerts/toast/toast2";

import SysTooltip2 from "@/components/utils/ui_v4/tooltips/SysTooltip2.vue";
import SysDataTable from "@/components/utils/dataTable/sysDataTable/Index.vue";
import api from "@/api/api";
import DialogEditarInfluenciaProduto
  from "@/components/produto/detalhes/ProjecaoVenda/Parametrizacao/AplicacaoTipoProjecao/CompraSazonal/components/dialog/DialogEditarInfluenciaProduto.vue";

const emit = defineEmits(['atualizar:produtos-influencia', 'atualizar:resumo-ajustes']);

const props = defineProps({
  step: {
    type: Number,
    required: true
  },
  produtos_selecionados: {
    type: Array,
    required: true
  },
});

watch(
  () => props.step,
  (newStep) => {
    if (newStep === 3) buscarInfluenciasProdutosSelecionados();
  }
);

const headers = [
  { text: 'Cod. Interno', value: 'id_produto' },
  { text: 'Produto', value: 'descricao_produto' },
  { text: 'Influência do item %', value: 'percentual_influencia' },
  { text: 'Ações', value: 'acoes' }
];

const formInicial = () => ({
  influencia_geral: null,
  produto: {}
});

const resumoInicial = () => ({
  itens_ajustados: itens_ajustados,
  representatividade_escopo: 0,
  impacto_adicional_ajuste: 0,
  influencia_total_estimada: 0,
});

const itens_ajustados = computed(() =>
  produtos_influencia.value.filter(i => i.ajustado === true).length
);

const resumo_ajustes = ref(resumoInicial());

const form_ref = ref(null);
const form_valido = ref(false);

const dialog_editar_influencia = ref(false);

const pesquisa_termo = ref('');
const produtos_influencia = ref([]);
const form = reactive(formInicial());

function buscarResumoAjustes() {
  const corpo = {
    ids_produtos_escopo: produtos_influencia.value.map(i => String(i.id_produto)),
    ids_produtos_ajustado: produtos_influencia.value.filter((i) => i.ajustado === true).map(i => String(i.id_produto)),
    percentual_ajustados: produtos_influencia.value.reduce((total, i) => {
      return total + Number(i.percentual_influencia || 0);
    }, 0)
  };

  api.post('eventos-sazonais/compra-sazonal/buscar-resumo-ajustes', corpo)
  .then((response) => {
    resumo_ajustes.value.representatividade_escopo = Number(response.data.representatividade_escopo);
    resumo_ajustes.value.impacto_adicional_ajuste = Number(response.data.impacto_adicional_ajuste);
    resumo_ajustes.value.influencia_total_estimada = Number(response.data.influencia_total_estimada);

    emit('atualizar:resumo-ajustes', resumo_ajustes);
  })
  .catch(() => toast2.error('Falha ao buscar os detalhes do produto selecionado.'));
}

function buscarInfluenciasProdutosSelecionados() {
  const corpo = {
    id_grupo: props.produtos_selecionados[0].id_grupo,
    ids_produtos: props.produtos_selecionados.map(i => String(i.id_produto)),
  };

  api.post('eventos-sazonais/compra-sazonal/buscar-influencias-produtos', corpo)
  .then((response) => {
    produtos_influencia.value = response.data.map(i => ({
      ...i,
      influencia_badge: 'Influência individual',
    }));

    emit('atualizar:produtos-influencia', produtos_influencia.value);
  })
  .catch(() => toast2.error('Falha ao buscar os detalhes do produto selecionado.'));
}

function aplicarInfluenciaGeral() {
  if (!form_valido.value) {
    toast2.error('Influência não aplicada', 'A influência deve ser válida e respeitar os limites definidos (mínimo -100%, máximo +100%)');
    return;
  }

  if (String(form.influencia_geral).includes(',')) {
    toast2.error('Influência não aplicada', "Use ponto '.' ao invés de vírgula ','");
    return;
  }

  let tipo_influencia = 'AUMENTO';
  if (Number(form.influencia_geral) < 0) tipo_influencia = 'REDUCAO';

  produtos_influencia.value = produtos_influencia.value.map((item) => {
    item.percentual_influencia = Number(form.influencia_geral);
    item.tipo_influencia = tipo_influencia;
    item.influencia_badge = 'Influência geral';
    item.ajustado = true;

    return item;
  });

  buscarResumoAjustes();

  emit('atualizar:produtos-influencia', produtos_influencia.value);

  form.influencia_geral = null;
  toast2.success('Influência aplicada com sucesso!');
}

function aplicarInfluenciaEspecifica(valor) {
  let tipo_influencia = 'AUMENTO';
  if (Number(valor) < 0) tipo_influencia = 'REDUCAO';

  produtos_influencia.value = produtos_influencia.value.map(i =>
    i.id_produto === form.produto.id_produto
      ? {
        ...i,
        percentual_influencia: Number(valor),
        influencia_badge: 'Influência individual',
        tipo_influencia: tipo_influencia,
        ajustado: true
      } : i
  );

  emit('atualizar:produtos-influencia', produtos_influencia.value);

  buscarResumoAjustes();

  toast2.success('Ajuste de item salvo com sucesso!');
  dialog_editar_influencia.value = false;
}

function editarItemInfluencia(item) {
  dialog_editar_influencia.value = true;
  form.produto = item;
}

function removerItemInfluencia(item) {
  produtos_influencia.value =
    produtos_influencia.value.filter(i => i.id_produto !== item.id_produto);

  buscarResumoAjustes();

  toast2.success('Ajuste de item removido com sucesso!');
}

const range = (msg = "") => (v) => {
  let n = v;

  if (typeof v === "string" && v.trim() === "") return msg;
  if (typeof n === "string") n = n.trim().replace("%", "").replace(",", ".");

  const num = Number(n);
  if (!Number.isFinite(num)) return msg;

  return num >= -100 && num <= 100 ? true : msg;
};

const rules = {
  rangeInfluencia: [range()]
};

function corLinha() {
  return { 'text-gray-400': 1 === 1 };
}

function caracteresNaoPermitidos(e) {
  if (e.key === ',') e.preventDefault();
  if (e.key === '%') e.preventDefault();
}

function badgeStyle(influencia) {
  const palavras = influencia.trim().split(/\s+/);
  const is_geral = palavras[palavras.length - 1].toLowerCase() === 'geral';

  if (is_geral) return { color: '#F2A202', backgroundColor: '#FAFAFA', padding: '8px 15px' };

  return { color: '#245AE5', backgroundColor: '#FAFAFA', padding: '8px 15px' };
}
</script>

<style scoped>
::v-deep(.input-clean.v-text-field--outlined .v-input__control > .v-input__slot) {
  background: #fff;
  border-radius: 4px;
  min-height: 50px;
}

::v-deep(.input-clean.v-text-field--outlined fieldset) {
  border-width: 1px;
  border-color: #CFD1D7;
}

::v-deep(.input-clean.v-text-field--outlined .v-label) {
  color: #73787F;
  text-align: center;
  margin-top: 5px;
  font-size: 14px;
}

::v-deep(.input-clean.v-text-field--outlined .v-input__append-inner) {
  min-height: 33px;
  display: flex;
  align-items: center;
}
</style>
